/**
 * usePagesScreen — estado, efeitos e ações da tela "Páginas e links".
 *
 * A TRADUÇÃO (resposta do servidor → ViewModel) mora em `pagesVmBuilder.js`, que é puro.
 * Aqui fica só o que precisa de React.
 *
 * Nome com sufixo `Screen` de propósito: já existe um `usePages` em `hooks/queries` que é o
 * acesso a dados (query + mutations) e que este hook CONSOME. Dois `usePages` seriam um convite
 * a importar o errado.
 */
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePages } from '../../hooks/queries';
import { useDomainAccess } from '../../hooks/useDomainAccess';
import { montarVm } from './pagesVmBuilder';

const URL_BASE = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';

/** int do catálogo PageType → slug da rota /pages/new/:pageType (o editor só entende slug). */
const SLUG_DO_TIPO = { 1: 'link_in_bio', 2: 'professional', 3: 'company' };

export default function usePagesScreen() {
  const navigate = useNavigate();

  const {
    pages, loading, isFetching, error, reload,
    publishPageAsync, unpublishPageAsync, deleteAsync,
  } = usePages();

  /**
   * O domínio próprio vem de OUTRO endpoint. É consulta opcional: se falhar, a tela perde só a
   * marcação de domínio e a oferta — nunca a lista de páginas, que é o motivo de existir aqui.
   */
  // MESMO hook do menu e da rota — mesma chave, então o React Query deduplica e as três
  // superfícies custam UMA requisição. `status` vem null quando a conta não foi ativada na
  // feature (204) ou quando o kill-switch de front está desligado; nos dois casos a tela some
  // com a oferta e a marcação de domínio, e segue mostrando as páginas normalmente.
  const { status: domainStatus } = useDomainAccess();

  /**
   * Estado dos diálogos. Cada um guarda o `id` do alvo mais `saving`/`error` próprios — o erro
   * fica DENTRO do diálogo para a pessoa não perder o contexto do que estava confirmando.
   */
  const [dialogos, setDialogos] = useState({});
  const [feedback, setFeedback] = useState(null);

  const abrir = useCallback((qual, dados) => {
    setDialogos((d) => ({ ...d, [qual]: dados }));
  }, []);

  const fechar = useCallback((qual) => {
    setDialogos((d) => ({ ...d, [qual]: null }));
  }, []);

  // A confirmação some sozinha; sem isso ela ficaria pendurada até o próximo clique.
  useEffect(() => {
    if (!feedback) return undefined;
    const t = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  const vm = montarVm({
    pages: loading && !pages?.length ? undefined : pages,
    domainStatus,
    carregando: loading,
    errorMessage: error ? 'Não foi possível carregar suas páginas agora.' : null,
    refreshing: isFetching && !loading,
    dialogos,
    feedback,
  });

  const acharPagina = (id) => vm.pages.find((p) => p.id === id);

  /** Executa uma mutação de diálogo, mantendo `saving`/`error` no próprio diálogo. */
  const executar = async (qual, id, fn, sucesso) => {
    setDialogos((d) => ({ ...d, [qual]: { ...d[qual], saving: true, error: null } }));
    try {
      await fn(id);
      setDialogos((d) => ({ ...d, [qual]: null }));
      setFeedback({ tone: 'success', message: sucesso });
    } catch (e) {
      setDialogos((d) => ({
        ...d,
        [qual]: { ...d[qual], saving: false, error: e?.message || 'Tente de novo em instantes.' },
      }));
    }
  };

  const copiar = async (texto, oQue) => {
    try {
      await navigator.clipboard.writeText(texto);
      setFeedback({ tone: 'success', message: `${oQue} copiado.` });
    } catch {
      // Copiar pode falhar por permissão do navegador — e falhar calado seria pior.
      setFeedback({ tone: 'error', message: 'Não foi possível copiar o link.' });
    }
  };

  const actions = {
    onRetry: reload,
    onRefresh: reload,

    onCreate: () => abrir('type', true),
    onCloseTypeDialog: () => fechar('type'),
    // A criação em si é do editor: ele conhece template, preset e o ciclo de rascunho.
    // A rota é /pages/new/:pageType e espera o SLUG, não o int do catálogo — o editor converte
    // de volta com `pageTypeStringToInt`. Mandar "3" aqui cairia no default (link_in_bio) e a
    // pessoa escolheria "Empresa" para ganhar um Link in Bio.
    onSelectType: (type) => { fechar('type'); navigate(`/pages/new/${SLUG_DO_TIPO[type] || 'link_in_bio'}`); },

    onEdit: (id) => navigate(`/pages/edit/${id}`),
    // Rascunho não tem o que pré-visualizar: o editor JÁ é a pré-visualização dele.
    onPreview: (id) => {
      const p = acharPagina(id);
      if (p?.status === 'draft') navigate(`/pages/edit/${id}`);
      else abrir('preview', { id });
    },
    onClosePreview: () => fechar('preview'),

    onPublish: (id) => abrir('publish', { id }),
    onCancelPublish: () => fechar('publish'),
    onConfirmPublish: () => executar(
      'publish', dialogos.publish?.id, publishPageAsync, 'Página publicada.'),

    onUnpublish: (id) => abrir('unpublish', { id }),
    onCancelUnpublish: () => fechar('unpublish'),
    onConfirmUnpublish: () => executar(
      'unpublish', dialogos.unpublish?.id, unpublishPageAsync, 'Página fora do ar.'),

    onDelete: (id) => abrir('delete', { id }),
    onCancelDelete: () => fechar('delete'),
    onConfirmDelete: () => executar(
      'delete', dialogos.delete?.id, deleteAsync, 'Página excluída.'),
    /**
     * A saída do diálogo bloqueado: despublica e MANTÉM o diálogo aberto, agora liberado.
     * Fechar aqui obrigaria a pessoa a reabrir o menu e clicar em excluir de novo — dois passos
     * para concluir o que ela já pediu.
     */
    onUnblockDelete: async () => {
      const id = dialogos.delete?.id;
      if (!id) return;
      setDialogos((d) => ({ ...d, delete: { ...d.delete, saving: true, error: null } }));
      try {
        await unpublishPageAsync(id);
        setDialogos((d) => ({ ...d, delete: { ...d.delete, saving: false } }));
        setFeedback({ tone: 'success', message: 'Página fora do ar. Agora pode ser excluída.' });
      } catch (e) {
        setDialogos((d) => ({
          ...d,
          delete: { ...d.delete, saving: false, error: e?.message || 'Não foi possível tirar do ar.' },
        }));
      }
    },

    onCopyUrl: (id) => copiar(`${URL_BASE}/${acharPagina(id)?.slug}`, 'Link'),
    // O endereço que o cliente REALMENTE entrega — por isso a mensagem diz qual foi.
    onCopyDomain: (id) => {
      const d = acharPagina(id)?.customDomain;
      if (d) copiar(d.url, d.domain);
    },
    onOpenPublic: (id) => abrirAba(acharPagina(id)?.publicUrl),
    onOpenDomain: (id) => abrirAba(acharPagina(id)?.customDomain?.url),

    onOpenDomainOffer: () => navigate('/domains'),
    onDismissFeedback: () => setFeedback(null),
  };

  return { vm, actions };
}

/** `noopener,noreferrer`: o link é conteúdo público do cliente e abre em aba nova. */
function abrirAba(url) {
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}
